import React, { PropsWithChildren, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Clip } from '@ff-os/rendering-shared/Clip';
import { Area } from '@ff-os/shared-utils/types/Area';
import { Size } from '@ff-os/shared-utils/types/Size';
import { noop } from 'lodash';
import { WindowStackItem } from '../../../context/window-stack/WindowStackItem';
import { useContain } from '../../../hooks/useContain';
import { useSize } from '../../../hooks/useSize';
import { AiStudioButton } from '../../ai-studio/AiStudioButton';
import { AiStudioWindow } from '../../ai-studio/AiStudioWindow';
import { LightSwitch } from '../../light-switch/LightSwitch';
import { WindowBody } from '../../window/partials/WindowBody';
import { CropTool } from '../crop-tool/CropTool';
import { PhotoEditButtonGroup } from '../photo-edit-button-group/PhotoEditButtonGroup';
import { rotateAndSliceCrop } from '../rotateAndSliceCrop';
import styles from './PhotoEditWindowBody.module.scss';

export const PhotoEditWindowBody = ({
    imageSize, activeCropBox, setCropBox,
    clip, aspectRatio: defaultAspectRatio,
    filter, updateFilter, overlayUrl, fileName,
    enableAiStudio, aiStudioStartupAppId,
    telemetryCategory, pluginId,
    uploadFiles, children
}: Props) => {

    const { t } = useTranslation();

    const containerRef = useRef<HTMLDivElement>(null);
    const containerSize = useSize(containerRef);
    const contentSize = useContain(containerSize, imageSize.width / imageSize.height);

    const [aspectRatio, setAspectRatio] = useState(defaultAspectRatio);
    const [openAiStudio, setOpenAiStudio] = useState(false);

    const canRotate = aspectRatio && aspectRatio !== 1;

    const rotateAspectRatio = useCallback(() => {
        const newAspectRatio = 1 / aspectRatio!;
        setAspectRatio(newAspectRatio);
        setCropBox(rotateAndSliceCrop(activeCropBox, imageSize, newAspectRatio));
    }, [aspectRatio, setCropBox, activeCropBox, imageSize]);

    return (
        <WindowBody disableScroll fullWidth className={styles.body}>
            <div ref={containerRef} className={styles.container}>
                <div style={contentSize}>
                    <CropTool
                        filter={filter}
                        overlayUrl={overlayUrl}
                        containerSize={contentSize}
                        activeCropBox={activeCropBox}
                        imageSize={imageSize}
                        onChange={setCropBox}
                        clip={clip}
                        aspectRatio={aspectRatio}>
                        {children}
                    </CropTool>
                </div>
            </div>

            <PhotoEditButtonGroup>
                {canRotate && (
                    <PhotoEditButtonGroup.Button
                        type='rotate'
                        onClick={rotateAspectRatio}
                        icon='general-ui-rotate-image'>
                        {t('commons.rotate')}
                    </PhotoEditButtonGroup.Button>
                )}

                {updateFilter && (
                    <PhotoEditButtonGroup.Button
                        onClick={() => updateFilter(filter !== 'grayscale' ? 'grayscale' : null)}>
                        <LightSwitch checked={filter === 'grayscale'} onChange={noop} />
                        {t('photo_filters.grayscale')}
                    </PhotoEditButtonGroup.Button>
                )}

                {(openAiStudio && uploadFiles) && (
                    <WindowStackItem>
                        <AiStudioWindow
                            onClose={() => setOpenAiStudio(false)}
                            aiStudioStartupAppId={aiStudioStartupAppId}
                            telemetryCategory={telemetryCategory}
                            uploadFiles={files => uploadFiles(files[0])}
                            image={{
                                name: fileName ?? '',
                                url: overlayUrl
                            }}
                            pluginId={pluginId ?? ''}
                        />
                    </WindowStackItem>
                )}

                {(enableAiStudio && uploadFiles) && (
                    <AiStudioButton onClick={() => setOpenAiStudio(true)} />
                )}
            </PhotoEditButtonGroup>
        </WindowBody>
    );
};

type Props = PropsWithChildren<{

    overlayUrl: string;
    imageSize: Size;
    fileName?: string;
    uploadFiles?: (file: File) => void;

    activeCropBox: Area;
    setCropBox: (cropBox: Area) => void;

    clip: Clip | undefined;
    aspectRatio?: number;

    filter: string | null;
    updateFilter: (filter: string | null) => void;


    telemetryCategory?: string;
    pluginId?: string;

    enableAiStudio?: boolean;
    aiStudioStartupAppId?: string;
}>;